import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import type { GeneratedArtifactPicker } from "~/entities/chat/types";
import { useChatArtifactFeed } from "~/entities/chat/useChatArtifactFeed";
import { isVideoUrl } from "~/shared/lib/uploads/media-kind";
import type { GalleryTarget } from "~/shared/store/useChatUIStore";
import { MediaLightbox, type MediaLightboxItem } from "~/shared/ui/media-lightbox";

interface ChatArtifactGalleryProps {
    chatId: string;
    /** Артефакт, на котором открыть галерею; null — галерея закрыта. */
    target: GalleryTarget | null;
    onClose: () => void;
}

function toSlide(a: GeneratedArtifactPicker): MediaLightboxItem {
    return { url: a.s3Url, type: a.artifactType === "video" ? "video" : "image" };
}

/**
 * Единая галерея всех сгенерированных объектов чата (image + video в одной
 * ленте). Открывается кликом по артефакту в ленте сообщений, листается
 * стрелками влево/вправо. Рендерится в body-портал, чтобы перекрыть ленту.
 *
 * Длина чата ничем не ограничена: лента приходит окном вокруг кликнутого объекта
 * и дочитывается при листании (`useChatArtifactFeed`), поэтому объект в конце
 * чата на десять тысяч генераций открывается на своём месте, с настоящими
 * соседями, а не в хвосте самых свежих. Счётчик показывает абсолютную позицию в
 * ленте, а не в загруженном окне.
 *
 * Предпросмотр обязан показывать именно то, на что нажали: пока окно грузится —
 * и если кликнутого объекта в ленте не оказалось вовсе (удалён, сменился
 * S3-бакет) — показывается он сам. Прежде в этом случае лента молча открывалась
 * на своём ПЕРВОМ элементе, и пользователь видел чужое медиа.
 *
 * Лента тянется с ОТКРЫТИЯ ЧАТА, а не с открытия предпросмотра: компонент
 * монтируется вместе с чатом, поэтому к моменту клика окно уже в руках и
 * счётчик со стрелками появляются сразу. Прежде запрос уходил по клику и стоял
 * в очереди за тяжёлыми медиа истории (шесть соединений на хост), а до его
 * ответа лайтбокс получал один кликнутый кадр без длины ленты — отсюда «1 из 1»
 * без стрелок, заметное именно в чатах с 4K.
 */
export function ChatArtifactGallery({ chatId, target, onClose }: ChatArtifactGalleryProps) {
    const open = target !== null;
    const { feed, prefetchAround } = useChatArtifactFeed({
        chatId,
        enabled: true,
        anchorId: target?.id,
    });

    // Держимся за ОБЪЕКТ, на котором стоим, а не за номер слайда: догрузка более
    // новых добавляет страницу в начало окна и сдвигает все номера, так что
    // листающего иначе выбрасывало бы обратно к якорю посреди просмотра.
    const [currentId, setCurrentId] = useState<string | null>(target?.id ?? null);

    useEffect(() => {
        setCurrentId(target?.id ?? null);
    }, [target?.id]);

    const handleIndexChange = useCallback(
        (index: number) => {
            const at = feed.items[index];
            if (at) setCurrentId(at.id);
            prefetchAround(index);
        },
        [feed.items, prefetchAround],
    );

    const slides = useMemo(() => feed.items.map(toSlide), [feed.items]);

    const clicked = useMemo<MediaLightboxItem | null>(
        () =>
            target === null ? null : { url: target.url, type: isVideoUrl(target.url) ? "video" : "image" },
        [target],
    );

    if (!open || clicked === null) return null;

    // Позиция считается по объекту, на котором стоим: после догрузки более новых
    // его номер в окне другой, а слайд обязан остаться прежним.
    const matchedIndex = feed.items.findIndex((a) => a.id === (currentId ?? target.id));
    const found = matchedIndex >= 0;

    return createPortal(
        <MediaLightbox
            items={found ? slides : [clicked, ...slides]}
            initialIndex={found ? matchedIndex : 0}
            indexOffset={found ? feed.offset : 0}
            totalCount={found ? feed.total : undefined}
            onIndexChange={found ? handleIndexChange : undefined}
            isOpen={open}
            onClose={onClose}
        />,
        document.body,
    );
}
